import React from 'react';
import { ArrowLeft, CheckCircle2, MessageCircle, Tag, ShieldCheck, User } from 'lucide-react';
import { Service } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

interface ConfirmationViewProps {
  businessName?: string;
  portalTheme?: string;
  brandColor?: string;
  primaryService: Service;
  selectedServices: Service[];
  assignedStaffName: string;
  formattedDateString: string;
  selectedTime: string;
  fullName: string;
  phone: string;
  isForAnotherPerson: boolean;
  anotherPersonName: string;
  onChangeIsForAnotherPerson: (val: boolean) => void;
  onChangeAnotherPersonName: (val: string) => void;
  onChangeClientData: () => void;
  onConfirmBooking: () => void;
  onBack: () => void;
  submitting: boolean;
  errorMessage?: string;
}

export const ConfirmationView: React.FC<ConfirmationViewProps> = ({
  businessName = 'Reservá tu turno',
  portalTheme,
  brandColor,
  primaryService,
  selectedServices,
  assignedStaffName,
  formattedDateString,
  selectedTime,
  fullName,
  phone,
  isForAnotherPerson,
  anotherPersonName,
  onChangeIsForAnotherPerson,
  onChangeAnotherPersonName,
  onChangeClientData,
  onConfirmBooking,
  onBack,
  submitting,
  errorMessage,
}) => {
  const theme = getThemeTokens(portalTheme, brandColor);
  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);

  const [showVoucherInput, setShowVoucherInput] = React.useState(false);
  const [voucherCode, setVoucherCode] = React.useState('');
  const [validatingVoucher, setValidatingVoucher] = React.useState(false);
  const [appliedVoucher, setAppliedVoucher] = React.useState<{ code: string; discountAmount: number; discountType: string } | null>(null);
  const [voucherError, setVoucherError] = React.useState('');

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim()) return;
    setValidatingVoucher(true);
    setVoucherError('');
    try {
      const res = await fetch('/api/vouchers/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: voucherCode.trim().toUpperCase(),
          total: totalCashPrice
        })
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedVoucher({
          code: data.code,
          discountAmount: data.discountAmount,
          discountType: data.discountType
        });
      } else {
        setVoucherError(data.error || 'Cupón inválido o expirado.');
      }
    } catch (e: any) {
      setVoucherError('Error validando cupón.');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const finalPrice = appliedVoucher ? Math.max(0, totalCashPrice - appliedVoucher.discountAmount) : totalCashPrice;

  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const displayPhone = `+549 ${cleanPhone}`;
  const initialLetter = fullName.trim().charAt(0).toUpperCase() || 'C';

  return (
    <div style={{ color: theme.textPrimary }} className="w-full max-w-5xl mx-auto px-4 pb-24">
      {/* Barra superior con nombre de negocio y botón volver */}
      <div className="flex items-center gap-3 mb-6 pt-2">
        <button
          onClick={onBack}
          aria-label="Volver"
          style={{
            backgroundColor: theme.bgSurface,
            borderColor: theme.borderSubtle,
            color: theme.textPrimary
          }}
          className="w-10 h-10 rounded-full border flex items-center justify-center hover:opacity-85 transition shadow-xs cursor-pointer shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1
          style={{ color: theme.textPrimary }}
          className="text-xl sm:text-2xl font-bold tracking-tight"
        >
          {businessName}
        </h1>
      </div>

      {/* Grid en 2 Columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: ¿Para quién es el turno? y Próximos Pasos */}
        <div className="lg:col-span-7">
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-8 sm:p-10 shadow-xs border flex flex-col gap-6"
          >
            {/* Mensaje de error si la reserva falla */}
            {errorMessage && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs sm:text-sm font-semibold">
                {errorMessage}
              </div>
            )}

            {/* Selector ¿Para quién es el turno? */}
            <div>
              <span
                style={{ color: theme.textSecondary }}
                className="block text-xs font-bold uppercase tracking-wider mb-2.5"
              >
                ¿Para quién es el turno?
              </span>

              {/* Dos pestañas: Para mí vs + Otra persona */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onChangeIsForAnotherPerson(false)}
                  style={{
                    backgroundColor: !isForAnotherPerson ? theme.brandButtonBg : theme.bgSubtle,
                    color: !isForAnotherPerson ? theme.brandButtonText : theme.textSecondary,
                    borderColor: theme.borderSubtle
                  }}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm border transition cursor-pointer shadow-2xs"
                >
                  Para mí
                </button>

                <button
                  type="button"
                  onClick={() => onChangeIsForAnotherPerson(true)}
                  style={{
                    backgroundColor: isForAnotherPerson ? theme.brandButtonBg : theme.bgSubtle,
                    color: isForAnotherPerson ? theme.brandButtonText : theme.textSecondary,
                    borderColor: theme.borderSubtle
                  }}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm border transition cursor-pointer shadow-2xs"
                >
                  + Otra persona
                </button>
              </div>
            </div>

            {/* Tarjeta del Cliente Titular */}
            <div
              style={{
                backgroundColor: theme.bgSubtle,
                borderColor: theme.borderSubtle
              }}
              className="border rounded-2xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div
                  style={{
                    backgroundColor: theme.isDark ? '#2E2E36' : '#E2E8F0',
                    color: theme.textPrimary
                  }}
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
                >
                  {initialLetter}
                </div>
                <div>
                  <span style={{ color: theme.textPrimary }} className="block font-bold text-sm">
                    {fullName}
                  </span>
                  <span style={{ color: theme.textSecondary }} className="block text-xs mt-0.5">
                    {displayPhone}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={onChangeClientData}
                style={{ color: theme.textSecondary }}
                className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-lg hover:opacity-80 transition cursor-pointer"
              >
                CAMBIAR
              </button>
            </div>

            {/* Input condicional si el turno es para otra persona */}
            {isForAnotherPerson && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col gap-1.5 animate-fade-in">
                <label className="text-xs font-bold text-slate-600">
                  Nombre de la otra persona (hijo/a, pareja, etc.)
                </label>
                <input
                  type="text"
                  required
                  value={anotherPersonName}
                  onChange={(e) => onChangeAnotherPersonName(e.target.value)}
                  placeholder="Ej: Lucas Perez"
                  className="w-full bg-white border border-slate-300 focus:border-[#0f172a] rounded-xl px-3.5 py-2.5 font-semibold text-xs sm:text-sm text-[#0f172a] focus:outline-none"
                />
              </div>
            )}

            {/* Próximos pasos (AppGenda Fase 2) */}
            <div className="flex flex-col gap-3 pt-1">
              <h3 className="text-sm font-bold text-[#0f172a]">
                Próximos pasos
              </h3>

              <div className="flex items-start gap-3 text-xs text-slate-600">
                <div className="w-7 h-7 rounded-full bg-[#1e293b] text-white flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck className="w-3.5 h-3.5" />
                </div>
                <p className="leading-relaxed font-medium">
                  Queda reservado a tu nombre, sin pagar nada ahora
                </p>
              </div>

              <div className="flex items-start gap-3 text-xs text-slate-600">
                <div className="w-7 h-7 rounded-full bg-[#10b981] text-white flex items-center justify-center shrink-0 mt-0.5">
                  <MessageCircle className="w-3.5 h-3.5 fill-white text-[#10b981]" />
                </div>
                <p className="leading-relaxed font-medium">
                  Reserva registrada en tiempo real en PostgreSQL. (Avisos por WhatsApp pendientes de conexión de API)
                </p>
              </div>
            </div>

            {/* Botón definitivo: Confirmar mi turno */}
            <button
              onClick={onConfirmBooking}
              disabled={submitting}
              style={{
                backgroundColor: theme.brandButtonBg,
                color: theme.brandButtonText,
                borderColor: theme.borderSubtle
              }}
              className={`w-full py-4 rounded-2xl font-bold text-base border transition duration-200 shadow-sm flex items-center justify-center gap-2 ${
                submitting
                  ? 'cursor-wait opacity-80'
                  : 'hover:opacity-90 cursor-pointer active:scale-[0.99]'
              }`}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  <span>Guardando tu reserva...</span>
                </>
              ) : (
                <span>Confirmar mi turno</span>
              )}
            </button>
          </div>
        </div>

        {/* Columna Derecha: Tarjeta Resumen */}
        <div className="lg:col-span-5">
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-6 shadow-xs border flex flex-col gap-4"
          >
            <div className="flex flex-col gap-3">
              {selectedServices.map((srv) => (
                <div key={srv.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      style={{ backgroundColor: theme.bgSubtle }}
                      className="w-12 h-12 rounded-xl overflow-hidden shrink-0"
                    >
                      <img
                        src={srv.imageUrl || 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=200&h=200&fit=crop'}
                        alt={srv.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3
                        style={{ color: theme.textPrimary }}
                        className="text-sm font-bold truncate"
                      >
                        {srv.name}
                      </h3>
                      <p style={{ color: theme.textSecondary }} className="text-xs">
                        👤 con {assignedStaffName}
                      </p>
                    </div>
                  </div>

                  <span
                    style={{ color: theme.textPrimary }}
                    className="font-bold text-sm whitespace-nowrap"
                  >
                    ${' '}
                    {srv.cashPrice.toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                backgroundColor: theme.bgSubtle,
                borderColor: theme.borderSubtle
              }}
              className="border rounded-xl p-3 flex items-center justify-between text-xs font-semibold"
            >
              <span style={{ color: theme.textMuted }}>Fecha y hora</span>
              <span style={{ color: theme.textPrimary }} className="font-bold">
                {formattedDateString} · {selectedTime} Hs
              </span>
            </div>

            <hr style={{ borderColor: theme.borderSubtle }} />

            {/* Subtotal si hay cupón */}
            {appliedVoucher && (
              <div style={{ color: theme.textSecondary }} className="flex items-center justify-between text-xs">
                <span>Subtotal lista</span>
                <span>${totalCashPrice.toLocaleString('es-AR')}</span>
              </div>
            )}

            {/* Descuento Cupón Aplicado */}
            {appliedVoucher && (
              <div className="flex items-center justify-between text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200/80">
                <span className="flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5" />
                  Cupón ({appliedVoucher.code})
                </span>
                <span>-${appliedVoucher.discountAmount.toLocaleString('es-AR')}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span style={{ color: theme.textPrimary }} className="text-sm font-bold">💵 Total</span>
              <span style={{ color: theme.textPrimary }} className="text-xl sm:text-2xl font-black">
                ${' '}
                {finalPrice.toLocaleString('es-AR', {
                  minimumFractionDigits: 0,
                })}
              </span>
            </div>

            {/* Formulario desplegable de Cupón */}
            {!appliedVoucher && (
              <div className="space-y-2 pt-1">
                {!showVoucherInput ? (
                  <button
                    type="button"
                    onClick={() => setShowVoucherInput(true)}
                    className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1.5 self-start cursor-pointer font-medium"
                  >
                    <Tag className="w-3.5 h-3.5 text-emerald-600" />
                    <span>¿Tenés un cupón de descuento?</span>
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="Ej: PRIMERAVEZ o BARBAVIP"
                        className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-600"
                      />
                      <button
                        type="button"
                        onClick={handleValidateVoucher}
                        disabled={validatingVoucher || !voucherCode.trim()}
                        className="px-3 py-1.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold rounded-xl transition disabled:opacity-50 cursor-pointer"
                      >
                        {validatingVoucher ? '...' : 'Aplicar'}
                      </button>
                    </div>
                    {voucherError && (
                      <span className="text-[11px] font-semibold text-rose-600 block">
                        {voucherError}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
